/**
 * ResetPasswordPage — public page at /reset-password
 *
 * Three render states:
 *   1. missing-token — no ?token param → "Invalid reset link" with back-to-login link
 *   2. expired       — GET validate returns 400 → "This link has expired"
 *   3. valid-form    — validate OK → password form
 *
 * On submit: POST /api/auth/reset-password → navigate /login?reset=success
 *
 * Design: reuses clinical-card shell from LoginPage.tsx
 */

import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { api, ApiError } from '@/lib/api';

// ---------------------------------------------------------------------------
// Password policy helpers (mirrors backend validatePasswordPolicy)
// ---------------------------------------------------------------------------

const SPECIAL_RE = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/;

function checkPolicy(password: string): string | null {
  if (password.length < 12) return 'At least 12 characters required.';
  if (!SPECIAL_RE.test(password)) return 'Must include at least one special character.';
  return null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type PageState = 'loading' | 'missing-token' | 'expired' | 'form' | 'submitting' | 'error';

interface ValidateResponse {
  data: { email: string };
}

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [pageState, setPageState] = useState<PageState>(token ? 'loading' : 'missing-token');
  const [doctorEmail, setDoctorEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Validate token on mount
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    api
      .get<ValidateResponse>(`/api/auth/reset-password/validate?token=${token}`)
      .then((res) => {
        if (!cancelled) {
          setDoctorEmail(res.data.email);
          setPageState('form');
        }
      })
      .catch(() => {
        if (!cancelled) setPageState('expired');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFieldError(null);
    setSubmitError(null);

    if (newPassword !== confirmPassword) {
      setFieldError('Passwords do not match.');
      return;
    }
    const policyErr = checkPolicy(newPassword);
    if (policyErr) {
      setFieldError(policyErr);
      return;
    }

    setPageState('submitting');
    try {
      await api.post('/api/auth/reset-password', { token, newPassword });
      navigate('/login?reset=success', { replace: true });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : 'Something went wrong. Please try again.';
      setSubmitError(msg);
      setPageState('error');
    }
  }

  // Derived submit guard
  const isFormReady = pageState === 'form' || pageState === 'error';
  const canSubmit =
    isFormReady &&
    newPassword.length >= 12 &&
    SPECIAL_RE.test(newPassword) &&
    confirmPassword.length > 0;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="min-h-screen bg-[var(--color-bg)] flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-full bg-clinical-charcoal flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-7 h-7 text-white"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
              />
            </svg>
          </div>
          <h1 className="font-heading text-2xl font-semibold text-clinical-charcoal leading-tight">
            Reset your password
          </h1>
          {pageState === 'form' && doctorEmail && (
            <p className="text-sm font-body text-clinical-text-muted mt-1">{doctorEmail}</p>
          )}
        </div>

        {/* ---- Loading ---- */}
        {pageState === 'loading' && (
          <div className="clinical-card p-8 flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-clinical-amber border-t-transparent rounded-full animate-spin" />
            <p className="text-sm font-body text-clinical-text-muted">Verifying link...</p>
          </div>
        )}

        {/* ---- Missing token ---- */}
        {pageState === 'missing-token' && (
          <div className="clinical-card p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-6 h-6 text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
                />
              </svg>
            </div>
            <h2 className="font-heading text-lg font-semibold text-clinical-charcoal mb-2">
              Invalid reset link
            </h2>
            <p className="text-sm font-body text-clinical-text-muted mb-6">
              This link is missing required information. Please request a new password reset link.
            </p>
            <Link
              to="/login"
              className="text-sm font-body text-clinical-teal hover:underline"
            >
              Back to sign in
            </Link>
          </div>
        )}

        {/* ---- Expired ---- */}
        {pageState === 'expired' && (
          <div className="clinical-card p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-6 h-6 text-clinical-amber"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                />
              </svg>
            </div>
            <h2 className="font-heading text-lg font-semibold text-clinical-charcoal mb-2">
              This link has expired
            </h2>
            <p className="text-sm font-body text-clinical-text-muted mb-6">
              Password reset links are valid for 24 hours and can only be used once. Please
              request a new link.
            </p>
            <Link
              to="/forgot-password"
              className="btn-primary inline-block text-center"
            >
              Request a new link
            </Link>
            <div className="mt-4">
              <Link
                to="/login"
                className="text-sm font-body text-clinical-teal hover:underline"
              >
                Back to sign in
              </Link>
            </div>
          </div>
        )}

        {/* ---- Form (valid token) ---- */}
        {(pageState === 'form' || pageState === 'submitting' || pageState === 'error') && (
          <div className="clinical-card p-6">
            <form onSubmit={handleSubmit} noValidate className="space-y-5">
              {/* New password */}
              <div>
                <label htmlFor="rp-new-password" className="form-label">
                  New password
                </label>
                <input
                  id="rp-new-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setFieldError(null);
                  }}
                  className="form-input"
                  placeholder="At least 12 chars + 1 special"
                  disabled={pageState === 'submitting'}
                />
                <p className="text-xs font-body text-clinical-text-muted mt-1.5">
                  Minimum 12 characters including at least one special character.
                </p>
              </div>

              {/* Confirm password */}
              <div>
                <label htmlFor="rp-confirm-password" className="form-label">
                  Confirm password
                </label>
                <input
                  id="rp-confirm-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setFieldError(null);
                  }}
                  className="form-input"
                  placeholder="Re-enter your new password"
                  disabled={pageState === 'submitting'}
                />
              </div>

              {/* Field error */}
              {fieldError && (
                <div
                  role="alert"
                  className="p-3 rounded-clinical bg-red-50 border border-red-200 text-sm font-body text-red-700"
                >
                  {fieldError}
                </div>
              )}

              {/* Submit error */}
              {submitError && pageState === 'error' && (
                <div
                  role="alert"
                  className="p-3 rounded-clinical bg-red-50 border border-red-200 text-sm font-body text-red-700"
                >
                  {submitError}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={!canSubmit}
                className="btn-primary w-full"
              >
                {pageState === 'submitting' ? (
                  <>
                    <span
                      className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
                      aria-hidden="true"
                    />
                    Setting password...
                  </>
                ) : (
                  'Set password'
                )}
              </button>
            </form>
          </div>
        )}

        {/* Back link (shown on form states) */}
        {(pageState === 'form' || pageState === 'submitting' || pageState === 'error') && (
          <p className="text-center text-sm font-body text-clinical-text-muted mt-6">
            <Link
              to="/login"
              className="text-clinical-teal hover:text-clinical-teal-dark underline-offset-2 hover:underline transition-colors"
            >
              Back to sign in
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}
